import { Link, useNavigate } from "react-router-dom";
import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { MapPin, Compass, ChevronDown, ChevronUp, ShieldCheck, HelpCircle, FlaskConical } from "lucide-react";
import { CATEGORY_COLORS, CATEGORY_ICONS } from "@/lib/constants";
import { useLang } from "@/lib/lang";
import { Button } from "@/components/ui/button";
import NavBar from "@/components/NavBar";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Explorer() {
  const { lang } = useLang();
  const nav = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [index, setIndex] = useState(0);
  const containerRef = useRef(null);
  const T = lang === "fr" ? FR : EN;

  const fetchFeed = async () => {
    try {
      const r = await axios.get(`${API}/explorer/feed?limit=25`);
      setItems(prev => [...prev, ...r.data.items]);
    } catch (e) { console.error(e); }
    setLoading(false);
  };

  useEffect(() => { fetchFeed(); }, []);

  // Auto-load more when reaching near the end
  useEffect(() => {
    if (items.length && index >= items.length - 3) fetchFeed();
  }, [index, items.length]);

  // Snap scroll handler
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const onScroll = () => {
      const h = el.clientHeight;
      const i = Math.round(el.scrollTop / h);
      if (i !== index) setIndex(i);
    };
    el.addEventListener("scroll", onScroll, { passive: true });
    return () => el.removeEventListener("scroll", onScroll);
  }, [index]);

  const scrollTo = (i) => {
    const el = containerRef.current;
    if (!el) return;
    el.scrollTo({ top: i * el.clientHeight, behavior: "smooth" });
  };

  return (
    <div className="min-h-screen bg-black text-white flex flex-col" data-testid="explorer-page">
      <NavBar variant="dark" />

      <div
        ref={containerRef}
        className="flex-1 overflow-y-scroll snap-y snap-mandatory"
        style={{ scrollbarWidth: "none", height: "calc(100vh - 64px)" }}
        data-testid="explorer-feed"
      >
        {loading && items.length === 0 && (
          <div className="h-full flex items-center justify-center text-zinc-400">{T.loading}</div>
        )}

        {items.map((it, i) => (
          <ExplorerCard key={`${it.id}-${i}`} item={it} lang={lang} nav={nav} T={T} />
        ))}
      </div>

      {/* Nav arrows for desktop */}
      {items.length > 0 && (
        <div className="fixed right-4 top-1/2 -translate-y-1/2 flex-col gap-2 hidden md:flex z-20">
          <button
            data-testid="explorer-prev-btn"
            onClick={() => scrollTo(Math.max(0, index - 1))}
            className="w-10 h-10 rounded-full bg-white/10 hover:bg-white/20 backdrop-blur flex items-center justify-center"
          >
            <ChevronUp size={20} />
          </button>
          <button
            data-testid="explorer-next-btn"
            onClick={() => scrollTo(index + 1)}
            className="w-10 h-10 rounded-full bg-white/10 hover:bg-white/20 backdrop-blur flex items-center justify-center"
          >
            <ChevronDown size={20} />
          </button>
        </div>
      )}
    </div>
  );
}

function ExplorerCard({ item, lang, nav, T }) {
  const color = CATEGORY_COLORS[item.category] || "#8b6f3d";
  const icon = CATEGORY_ICONS[item.category] || "✨";
  const name = item.name?.[lang] || item.name?.fr;
  const desc = item.description?.[lang] || item.description?.fr;
  const mediaUrl = item.first_media_id ? `${API}/media/${item.first_media_id}` : null;
  const isFiction = item.ai_generated || item.category === "ia";

  return (
    <section
      className="snap-start relative h-full w-full flex items-center justify-center overflow-hidden"
      style={{ height: "calc(100vh - 64px)" }}
      data-testid={`explorer-card-${item.id}`}
    >
      {/* Background media or gradient */}
      <div className="absolute inset-0">
        {mediaUrl && item.first_media_kind === "image" ? (
          <img src={mediaUrl} alt="" className="w-full h-full object-cover opacity-70" />
        ) : (
          <div
            className="w-full h-full"
            style={{
              background: `radial-gradient(circle at 30% 30%, ${color}55, transparent 60%), radial-gradient(circle at 70% 70%, ${color}33, transparent 60%), linear-gradient(180deg, #0a0a0a, #1a1a1a)`,
            }}
          />
        )}
        <div className="absolute inset-0 bg-gradient-to-t from-black via-black/50 to-transparent" />
      </div>

      {/* Content overlay */}
      <div className="relative z-10 max-w-2xl w-full px-6 md:px-12 pb-16">
        <div className="flex items-center gap-2 mb-4">
          <span
            className="inline-flex items-center justify-center w-10 h-10 rounded-full text-lg shadow-lg"
            style={{ background: color, color: "#fff" }}
          >
            {icon}
          </span>
          <span
            className="font-mono-data text-[10px] uppercase tracking-[0.3em] px-2 py-1 rounded-full border"
            style={{ borderColor: color, color: "#fff", background: `${color}44` }}
          >
            {item.category}
          </span>
          {item.confirmed && (
            <span className="flex items-center gap-1 text-[10px] text-emerald-300"><ShieldCheck size={11} /> {T.confirmed}</span>
          )}
          {isFiction && (
            <span className="flex items-center gap-1 text-[10px] text-pink-300"><FlaskConical size={11} /> {T.hypothesis}</span>
          )}
          {!item.confirmed && !isFiction && (
            <span className="flex items-center gap-1 text-[10px] text-amber-300"><HelpCircle size={11} /> {T.legend}</span>
          )}
        </div>

        <h2 className="font-serif-mystery text-3xl md:text-5xl leading-tight mb-3 text-white drop-shadow-lg" data-testid={`explorer-title-${item.id}`}>
          {name}
        </h2>
        {item.country && (
          <div className="flex items-center gap-1 text-white/80 text-sm mb-4">
            <MapPin size={13} /> {item.country}
          </div>
        )}
        <p className="text-white/90 text-base md:text-lg leading-relaxed mb-6 max-w-xl line-clamp-6" data-testid={`explorer-desc-${item.id}`}>
          {desc}
        </p>

        <Button
          data-testid={`explorer-view-map-${item.id}`}
          onClick={() => nav(`/map?loc=${item.id}`)}
          className="bg-[#8b6f3d] hover:bg-[#a1854b] text-white rounded-full h-12 px-6 shadow-xl"
        >
          <Compass size={16} className="mr-2" />
          {T.viewOnMap}
        </Button>
      </div>

      {/* Floating hint on first card */}
      <div className="absolute top-4 left-1/2 -translate-x-1/2 text-white/60 text-xs font-mono-data uppercase tracking-widest hidden md:block">
        {T.scrollHint}
      </div>
    </section>
  );
}

const FR = {
  loading: "Chargement du fil…",
  viewOnMap: "Voir sur la carte",
  confirmed: "Confirmé",
  hypothesis: "Hypothèse IA",
  legend: "Légende",
  scrollHint: "↕ Faites défiler pour découvrir",
};
const EN = {
  loading: "Loading feed…",
  viewOnMap: "View on map",
  confirmed: "Confirmed",
  hypothesis: "AI hypothesis",
  legend: "Legend",
  scrollHint: "↕ Scroll to discover",
};
